import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/ui/alert';
import { Button } from '@repo/ui/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@repo/ui/components/ui/card';
import { Input } from '@repo/ui/components/ui/input';
import { Label } from '@repo/ui/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/ui/select';
import { PageHeader } from '@repo/ui/ui/layout/page-header';
import { Lock, Play, RefreshCw, RotateCcw } from 'lucide-react';
import {
  advancePaymentLifecycleTime,
  fetchPaymentLifecycleStatus,
  PAYMENT_DEBUG_SECRET_STORAGE_KEY as DEBUG_SECRET_STORAGE_KEY,
  resetPaymentLifecycle,
  runPaymentLifecycleCron,
  simulatePaymentFailure,
  verifyPaymentDebugSecret,
  type PaymentLifecycleStatus,
} from '../../../lib/payment-lifecycle-debug-api';

type CronStep = 'suspend' | 'deletion-warning' | 'delete';

export default function PaymentLifecycleDebugPage() {
  const [debugSecret, setDebugSecret] = useState<string | null>(null);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [groupeId, setGroupeId] = useState('');
  const [paymentFailedDaysAgo, setPaymentFailedDaysAgo] = useState('11');
  const [suspendedDaysAgo, setSuspendedDaysAgo] = useState('31');
  const [cronStep, setCronStep] = useState<CronStep>('suspend');
  const [status, setStatus] = useState<PaymentLifecycleStatus | null>(null);
  const [lastResponse, setLastResponse] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    const stored = sessionStorage.getItem(DEBUG_SECRET_STORAGE_KEY);
    if (stored) setDebugSecret(stored);
  }, []);

  const handleUnlock = useCallback(async () => {
    setPasswordError(null);
    if (!passwordInput.trim()) {
      setPasswordError('Saisissez le mot de passe.');
      return;
    }
    setIsUnlocking(true);
    try {
      await verifyPaymentDebugSecret(passwordInput);
      sessionStorage.setItem(DEBUG_SECRET_STORAGE_KEY, passwordInput);
      setDebugSecret(passwordInput);
      setPasswordInput('');
    } catch {
      setPasswordError('Mot de passe incorrect ou accès debug indisponible.');
    } finally {
      setIsUnlocking(false);
    }
  }, [passwordInput]);

  const handleLock = useCallback(() => {
    sessionStorage.removeItem(DEBUG_SECRET_STORAGE_KEY);
    setDebugSecret(null);
    setStatus(null);
    setLastResponse(null);
  }, []);

  const parseGroupeId = useCallback((): number | null => {
    const id = Number.parseInt(groupeId, 10);
    if (Number.isNaN(id) || id <= 0) return null;
    return id;
  }, [groupeId]);

  const runAction = useCallback(
    async (action: () => Promise<unknown>) => {
      if (!debugSecret) return;
      const id = parseGroupeId();
      setActionError(null);
      setIsLoading(true);
      try {
        const result = await action();
        setLastResponse(result);
        if (id != null && 'subscriptionStatus' in (result as PaymentLifecycleStatus)) {
          setStatus(result as PaymentLifecycleStatus);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Erreur inconnue';
        setActionError(message);
      } finally {
        setIsLoading(false);
      }
    },
    [debugSecret, parseGroupeId],
  );

  const handleLoadStatus = useCallback(async () => {
    const id = parseGroupeId();
    if (!debugSecret || id == null) {
      setActionError('Indiquez un ID groupe valide.');
      return;
    }
    await runAction(() => fetchPaymentLifecycleStatus(id, debugSecret));
  }, [debugSecret, parseGroupeId, runAction]);

  if (!debugSecret) {
    return (
      <div className="container mx-auto flex min-h-[60vh] max-w-md flex-col justify-center py-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Accès debug paiement
            </CardTitle>
            <CardDescription>
              Connecté en tant qu&apos;admin. Saisissez le mot de passe root pour accéder aux
              outils de simulation du cycle de relance.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="debug-password">Mot de passe</Label>
              <Input
                id="debug-password"
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void handleUnlock()}
                autoComplete="off"
              />
            </div>
            {passwordError ? (
              <Alert variant="destructive">
                <AlertDescription>{passwordError}</AlertDescription>
              </Alert>
            ) : null}
            <Button className="w-full" disabled={isUnlocking} onClick={() => void handleUnlock()}>
              {isUnlocking ? 'Vérification...' : 'Accéder'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto space-y-6 py-6">
      <PageHeader>
        <PageHeader.Title>Debug — Cycle paiement</PageHeader.Title>
        <PageHeader.Description>
          Simulation du cycle PAST_DUE → SUSPENDED → EXPIRED (forfaits payants uniquement).
        </PageHeader.Description>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/superadmin/debug/payment-stats">Statistiques paiements</Link>
          </Button>
          <Button variant="outline" size="sm" onClick={handleLock}>
            Verrouiller
          </Button>
        </div>
      </PageHeader>

      <Alert>
        <AlertTitle>Environnement de test</AlertTitle>
        <AlertDescription>
          Utilisez un groupe de test (BASIC/ADVANCED). Les emails partent vers les vraies adresses
          du groupe.
        </AlertDescription>
      </Alert>

      {actionError ? (
        <Alert variant="destructive">
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Groupe cible</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div className="space-y-2">
            <Label htmlFor="groupe-id">ID groupe</Label>
            <Input
              id="groupe-id"
              type="number"
              min={1}
              value={groupeId}
              onChange={(e) => setGroupeId(e.target.value)}
              className="w-40"
            />
          </div>
          <Button variant="secondary" disabled={isLoading} onClick={() => void handleLoadStatus()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Charger le statut
          </Button>
        </CardContent>
      </Card>

      {status ? (
        <Card>
          <CardHeader>
            <CardTitle>Statut actuel</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="overflow-auto rounded-md bg-muted p-4 text-sm">
              {JSON.stringify(status, null, 2)}
            </pre>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>J0 — Échec de paiement</CardTitle>
            <CardDescription>PAST_DUE + email &quot;Mettez à jour votre carte&quot;</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              disabled={isLoading || !groupeId}
              onClick={() => {
                const id = parseGroupeId();
                if (!id || !debugSecret) return;
                void runAction(() => simulatePaymentFailure(id, debugSecret));
              }}
            >
              <Play className="mr-2 h-4 w-4" />
              Simuler échec
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Avancer le temps</CardTitle>
            <CardDescription>Décale les dates en arrière pour déclencher les crons</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <Label>Jours depuis échec (ex. 11 → suspension)</Label>
              <Input
                type="number"
                min={0}
                value={paymentFailedDaysAgo}
                onChange={(e) => setPaymentFailedDaysAgo(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Jours depuis suspension (ex. 31 → warning)</Label>
              <Input
                type="number"
                min={0}
                value={suspendedDaysAgo}
                onChange={(e) => setSuspendedDaysAgo(e.target.value)}
              />
            </div>
            <Button
              variant="secondary"
              disabled={isLoading || !groupeId}
              onClick={() => {
                const id = parseGroupeId();
                if (!id || !debugSecret) return;
                void runAction(() =>
                  advancePaymentLifecycleTime(id, debugSecret, {
                    paymentFailedDaysAgo: Number(paymentFailedDaysAgo) || undefined,
                    suspendedDaysAgo: Number(suspendedDaysAgo) || undefined,
                  }),
                );
              }}
            >
              Appliquer
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Forcer un cron</CardTitle>
            <CardDescription>Exécute immédiatement une étape planifiée (9h)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Select value={cronStep} onValueChange={(v) => setCronStep(v as CronStep)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="suspend">suspend (J+10)</SelectItem>
                <SelectItem value="deletion-warning">deletion-warning (J+40)</SelectItem>
                <SelectItem value="delete">delete (J+70)</SelectItem>
              </SelectContent>
            </Select>
            <Button
              disabled={isLoading}
              onClick={() => {
                if (!debugSecret) return;
                void runAction(() => runPaymentLifecycleCron(cronStep, debugSecret));
              }}
            >
              <Play className="mr-2 h-4 w-4" />
              Déclencher
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Réinitialiser</CardTitle>
            <CardDescription>ACTIVE + effacement des champs lifecycle</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              disabled={isLoading || !groupeId}
              onClick={() => {
                const id = parseGroupeId();
                if (!id || !debugSecret) return;
                void runAction(() => resetPaymentLifecycle(id, debugSecret));
              }}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset ACTIVE
            </Button>
          </CardContent>
        </Card>
      </div>

      {lastResponse != null ? (
        <Card>
          <CardHeader>
            <CardTitle>Dernière réponse API</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="overflow-auto rounded-md bg-muted p-4 text-sm">
              {JSON.stringify(lastResponse, null, 2)}
            </pre>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
